<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  CheckCircle2,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  FilePlus2,
  Plus,
  Pencil,
  Save,
  Send,
  Trash2,
} from "lucide-vue-next";
import type { Project, ProjectRequirement, ProjectTask, V2Asset } from "@/types";
import { useProjectStore } from "@/stores/projects";
import { v2 } from "@/services/v2";
import AssetPreviewModal from "@/components/AssetPreviewModal.vue";
import ExecutionTables from "@/components/ExecutionTables.vue";
import MarkdownContent from "@/components/MarkdownContent.vue";

const props = defineProps<{ project: Project }>();
const emit = defineEmits<{ submitted: []; changed: [] }>();
const { t } = useI18n();
const store = useProjectStore();
const title = ref("");
const plannedStartAt = ref("");
const plannedEndAt = ref("");
const newTaskTitle = ref("");
const newGroupTitles = ref<Record<number, string>>({});
const newChildTitles = ref<Record<number, string>>({});
const taskForms = ref<Record<number, { title: string; scope: string; acceptance: string; plannedStartAt: string; plannedEndAt: string }>>({});
const todoForms = ref<Record<number, { title: string; description: string; acceptance: string; plannedStartAt: string; plannedEndAt: string }>>({});
const requirements = ref<ProjectRequirement[]>([]);
const saving = ref(false);
const previewAsset = ref<V2Asset | null>(null);
const draftPage = ref<HTMLElement | null>(null);
const mobilePage = ref(0);
const editingCopies = ref<Record<number, boolean>>({});
const taskPageStart = 3;
const submitPageIndex = computed(() => taskPageStart + props.project.tasks.length);
const mobilePageCount = computed(() => submitPageIndex.value + 1);
const mobileProgress = computed(() =>
  Math.round(((mobilePage.value + 1) / mobilePageCount.value) * 100),
);
const activeTaskIndex = computed(() =>
  Math.min(
    Math.max(mobilePage.value - taskPageStart, 0),
    Math.max(props.project.tasks.length - 1, 0),
  ),
);
const mobilePageLabel = computed(() => {
  if (mobilePage.value === 0) return t("draftEditor.infoPage");
  if (mobilePage.value === 1) return t("draftEditor.solutionPage");
  if (mobilePage.value === 2) return t("draftEditor.requirementsPage");
  if (mobilePage.value === submitPageIndex.value) return t("draftEditor.submitPage");
  return t("draftEditor.itemPage", { index: activeTaskIndex.value + 1, title: taskForms.value[props.project.tasks[activeTaskIndex.value]?.id]?.title || t("draftEditor.executionTask") });
});
const validation = computed(() => store.validation);
const reviewedTaskCount = computed(() =>
  props.project.tasks.filter((task) => Boolean(task.client_reviewed)).length,
);
const isPosterProject = computed(() =>
  /(海报|instagram|\big\s*(?:post|贴文)|poster|social\s*post)/i.test(
    [
      props.project.title,
      ...props.project.tasks.map((task) => `${task.title} ${task.client_title || ""}`),
    ].join(" "),
  ),
);
const itemNoun = computed(() => t(isPosterProject.value ? "draftEditor.itemPoster" : "draftEditor.itemTask"));
const itemUnit = computed(() => t(isPosterProject.value ? "draftEditor.unitPoster" : "draftEditor.unitTask"));
const autosizeHandlers = new WeakMap<HTMLTextAreaElement, () => void>();
function resizeTextarea(element: HTMLTextAreaElement) {
  element.style.height = "auto";
  element.style.height = `${Math.max(element.scrollHeight + 2, 72)}px`;
}
const vAutosize = {
  mounted(element: HTMLTextAreaElement) {
    const handler = () => resizeTextarea(element);
    autosizeHandlers.set(element, handler);
    element.addEventListener("input", handler);
    requestAnimationFrame(handler);
  },
  updated(element: HTMLTextAreaElement) {
    resizeTextarea(element);
  },
  unmounted(element: HTMLTextAreaElement) {
    const handler = autosizeHandlers.get(element);
    if (handler) element.removeEventListener("input", handler);
    autosizeHandlers.delete(element);
  },
};
function lines(value: string) {
  return value.split("\n").map((item) => item.trim()).filter(Boolean);
}
function posterCopyParts(value: string) {
  const matches = [...value.matchAll(/(?:^|\n)(Header|Subheader|Button)\s*\n([\s\S]*?)(?=\n\s*(?:Header|Subheader|Button)\s*\n|$)/gi)];
  if (!matches.length) return [{ label: "Content", text: value.trim() }];
  return matches.map((match) => ({ label: match[1], text: match[2].trim() }));
}
function syncForms() {
  title.value = props.project.title;
  plannedStartAt.value = props.project.planned_start_at || "";
  plannedEndAt.value = props.project.planned_end_at || props.project.expected_at || "";
  taskForms.value = Object.fromEntries(
    props.project.tasks.map((task) => [
      task.id,
      {
        title: task.client_title || task.title,
        scope: task.client_scope || task.scope,
        acceptance: (task.client_acceptance_criteria?.length
          ? task.client_acceptance_criteria
          : task.acceptance_criteria || []
        ).join("\n"),
        plannedStartAt: task.planned_start_at || "",
        plannedEndAt: task.planned_end_at || task.expected_at || "",
      },
    ]),
  );
  todoForms.value = Object.fromEntries(
    props.project.tasks.flatMap((task) =>
      task.checklist.map((item) => [
        item.id,
        {
          title: item.title,
          description: item.description,
          acceptance: item.acceptance_criteria.join("\n"),
          plannedStartAt: item.planned_start_at || "",
          plannedEndAt: item.planned_end_at || "",
        },
      ]),
    ),
  );
  requirements.value = (props.project.reviewRequirements || []).map((item) => ({ ...item }));
}
watch(() => [props.project.id, props.project.updated_at, props.project.tasks.length], syncForms, { immediate: true });
watch(
  () => props.project.id,
  () => {
    mobilePage.value = 0;
  },
);

async function showMobilePage(index: number) {
  mobilePage.value = Math.min(Math.max(index, 0), mobilePageCount.value - 1);
  await nextTick();
  draftPage.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

async function run(action: () => Promise<unknown>) {
  saving.value = true;
  try {
    await action();
    emit("changed");
  } finally {
    saving.value = false;
  }
}
async function saveMeta() {
  await run(() =>
    store.updateDraft({
      title: title.value,
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
      rowVersion: props.project.row_version,
    }),
  );
}
async function saveRequirements() {
  await run(() =>
    store.updateDraft({
      title: title.value,
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
      reviewRequirements: requirements.value,
      rowVersion: props.project.row_version,
    }),
  );
}
function addRequirement() {
  requirements.value.push({
    id: `manual-${Date.now()}`,
    category: "attention",
    title: "",
    description: "",
    confirmed: false,
  });
}
function removeRequirement(id: string) {
  requirements.value = requirements.value.filter((item) => item.id !== id);
}
async function saveTask(taskId: number, rowVersion: number, clientReviewed = false) {
  const form = taskForms.value[taskId];
  if (!form) return;
  await run(() =>
    store.updateTask(taskId, {
      title: form.title,
      scope: form.scope,
      acceptanceCriteria: lines(form.acceptance),
      clientAcceptanceCriteria: lines(form.acceptance),
      plannedStartAt: form.plannedStartAt || null,
      plannedEndAt: form.plannedEndAt || null,
      clientReviewed,
      rowVersion,
    }),
  );
}
async function confirmTask(task: ProjectTask) {
  editingCopies.value[task.id] = false;
  await saveTask(task.id, task.row_version, !Boolean(task.client_reviewed));
}
async function addTask() {
  if (!newTaskTitle.value.trim()) return;
  const value = newTaskTitle.value;
  newTaskTitle.value = "";
  await run(() =>
    store.addTask({
      title: value,
      scope: "",
      deliverables: [],
      acceptanceCriteria: ["交付内容与确认方案一致"],
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
    }),
  );
}
async function saveTodo(itemId: number, rowVersion: number) {
  const form = todoForms.value[itemId];
  if (!form) return;
  await run(() =>
    store.updateChecklist(itemId, {
      title: form.title,
      description: form.description,
      acceptanceCriteria: lines(form.acceptance),
      plannedStartAt: form.plannedStartAt || null,
      plannedEndAt: form.plannedEndAt || null,
      rowVersion,
    }),
  );
}
async function addTodo(taskId: number, parentItemId?: number) {
  const source = parentItemId ? newChildTitles : newGroupTitles;
  const key = parentItemId || taskId;
  const value = source.value[key]?.trim();
  if (!value) return;
  source.value[key] = "";
  await run(() =>
    store.addChecklist(taskId, {
      title: value,
      parentItemId: parentItemId || null,
      description: parentItemId
        ? `完成“${value}”并提交可核查结果。`
        : `完成“${value}”阶段，并确保所有子步骤可核查。`,
      acceptanceCriteria: ["按客户确认要求完成并通过核查"],
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
    }),
  );
}
function checklistGroups(task: ProjectTask) {
  return task.checklist.filter((item) => !item.parent_item_id);
}
function checklistChildren(task: ProjectTask, parentId: number) {
  return task.checklist.filter((item) => Number(item.parent_item_id) === parentId);
}
async function uploadTaskAsset(taskId: number, event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  await run(async () => {
    await v2.uploadTaskAsset(taskId, file);
    await store.loadDraft(props.project.id);
  });
  input.value = "";
}
async function archiveConversationAsset(assetId: number, taskId: number) {
  await run(async () => {
    await v2.linkAssetToTask(assetId, taskId);
    await store.loadDraft(props.project.id);
  });
}
async function submit() {
  await run(async () => {
    await store.submit();
    emit("submitted");
  });
}
</script>

<template>
  <div ref="draftPage" class="draft-page">
    <header class="draft-head">
      <div>
        <span>{{ t("draftEditor.title") }}</span>
        <h1>{{ project.title }}</h1>
        <p>{{ t("draftEditor.hint") }}</p>
      </div>
      <button class="btn btn--primary submit desktop-submit" :disabled="saving || !validation.valid" @click="submit">
        <Send :size="17" />{{ t("draftEditor.submitReview") }}
      </button>
    </header>

    <section class="mobile-page-status" aria-live="polite">
      <div><span>{{ mobilePageLabel }}</span><b>{{ mobilePage + 1 }} / {{ mobilePageCount }}</b></div>
      <div class="mobile-progress-track"><i :style="{ width: `${mobileProgress}%` }"></i></div>
    </section>

    <section class="meta-band draft-mobile-section" :class="{ 'mobile-active': mobilePage === 0 }">
      <label>{{ t("draftEditor.projectName") }}<input v-model="title" class="input" /></label>
      <label>{{ t("draftEditor.start") }}<input v-model="plannedStartAt" class="input" type="date" /></label>
      <label>{{ t("draftEditor.expectedEnd") }}<input v-model="plannedEndAt" class="input" type="date" /></label>
      <button class="btn" :disabled="saving" @click="saveMeta"><Save :size="16" />{{ t("draftEditor.saveInfo") }}</button>
    </section>

    <section v-if="!validation.valid" class="validation desktop-validation">
      <b>{{ t("draftEditor.validationMissing", { count: validation.errors.length }) }}</b>
      <ul><li v-for="error in validation.errors" :key="`${error.code}-${error.taskId || 0}`">{{ error.message }}</li></ul>
    </section>
    <section v-else class="validation valid desktop-validation"><CheckCircle2 :size="18" /><b>{{ t("draftEditor.validationPassed") }}</b></section>

    <section class="solution-band draft-mobile-section" :class="{ 'mobile-active': mobilePage === 1 }">
      <h2>{{ t("draftEditor.confirmedSolution") }}</h2>
      <MarkdownContent :content="project.solution?.clientContent.fullPlan || project.solution?.clientContent.summary" />
    </section>

    <section class="requirements-review draft-mobile-section" :class="{ 'mobile-active': mobilePage === 2 }">
      <header>
        <div><h2>{{ t("draftEditor.requirements") }}</h2><p>{{ t("draftEditor.requirementsHint") }}</p></div>
        <button class="btn" type="button" @click="addRequirement"><Plus :size="16" />{{ t("draftEditor.addRequirement") }}</button>
      </header>
      <div v-for="requirement in requirements" :key="requirement.id" class="requirement-row">
        <select v-model="requirement.category" class="select" :aria-label="t('draftEditor.requirementCategory')">
          <option value="attention">{{ t("draftEditor.attention") }}</option><option value="content">{{ t("draftEditor.content") }}</option>
          <option value="dialogue">{{ t("draftEditor.dialogue") }}</option><option value="theme">{{ t("draftEditor.theme") }}</option>
          <option value="other">{{ t("draftEditor.other") }}</option>
        </select>
        <input v-model="requirement.title" class="input" :placeholder="t('draftEditor.requirementTitle')" />
        <textarea v-model="requirement.description" v-autosize class="textarea" :placeholder="t('draftEditor.requirementDescription')"></textarea>
        <label class="confirm-check"><input v-model="requirement.confirmed" type="checkbox" />{{ t("draftEditor.verified") }}</label>
        <button class="icon-btn danger" :title="t('draftEditor.deleteRequirement')" @click="removeRequirement(requirement.id)"><Trash2 :size="16" /></button>
      </div>
      <button class="btn btn--primary save-requirements" :disabled="saving" @click="saveRequirements"><Save :size="16" />{{ t("draftEditor.saveRequirements") }}</button>
    </section>

    <section
      class="tasks draft-mobile-section"
      :class="{ 'mobile-active': mobilePage >= taskPageStart && mobilePage < submitPageIndex }"
    >
      <header><div><h2>{{ t("draftEditor.taskList", { item: itemNoun }) }}</h2><p>{{ t("draftEditor.reviewed", { done: reviewedTaskCount, total: project.tasks.length, unit: itemUnit }) }}</p></div></header>
      <article
        v-for="(task, index) in project.tasks"
        :key="task.id"
        class="task-block"
        :class="{ 'mobile-active': mobilePage === taskPageStart + index }"
      >
        <header><span>{{ index + 1 }}</span><div><b>{{ taskForms[task.id]?.title }}</b><small>{{ t("common.fromTo", { start: task.planned_start_at, end: task.planned_end_at }) }}</small></div><button class="icon-btn danger" :title="t('draftEditor.deleteTask', { item: itemNoun })" @click="run(() => store.deleteTask(task.id))"><Trash2 :size="16" /></button></header>
        <div class="poster-fields">
          <label>{{ t("draftEditor.taskName", { item: itemNoun }) }}<input v-model="taskForms[task.id].title" class="input" /></label>
          <label class="source-field desktop-source-field">{{ t("draftEditor.sourceCopy") }}<textarea v-model="taskForms[task.id].scope" v-autosize class="textarea" spellcheck="false"></textarea><small>{{ t("draftEditor.sourceCopyHint") }}</small></label>
          <section class="mobile-source-card">
            <header><div><span>{{ t("draftEditor.finalCopy") }}</span><small>{{ t("draftEditor.preserveOriginal") }}</small></div><button class="icon-btn" type="button" :title="editingCopies[task.id] ? t('draftEditor.finishEdit') : t('draftEditor.editCopy')" @click="editingCopies[task.id] = !editingCopies[task.id]"><Check v-if="editingCopies[task.id]" :size="17" /><Pencil v-else :size="17" /></button></header>
            <textarea v-if="editingCopies[task.id]" v-model="taskForms[task.id].scope" v-autosize class="textarea mobile-copy-editor" spellcheck="false"></textarea>
            <div v-else class="poster-copy">
              <section v-for="part in posterCopyParts(taskForms[task.id].scope)" :key="part.label"><span>{{ part.label }}</span><p>{{ part.text }}</p></section>
            </div>
          </section>
          <section class="team-brief"><span>{{ t("draftEditor.teamBrief") }}</span><p>{{ task.scope }}</p></section>
          <ExecutionTables :tables="task.execution_tables" :skill-id="project.skill_id" />
          <label>{{ t("draftEditor.start") }}<input v-model="taskForms[task.id].plannedStartAt" class="input" type="date" /></label>
          <label>{{ t("draftEditor.end") }}<input v-model="taskForms[task.id].plannedEndAt" class="input" type="date" /></label>
          <button class="btn save-task" :disabled="saving" @click="saveTask(task.id, task.row_version)"><Save :size="16" />{{ t("draftEditor.saveTask", { item: itemNoun }) }}</button>
        </div>
        <section class="assets">
          <header><div><h3>{{ t("draftEditor.optionalAssets", { item: itemNoun }) }}</h3><small>{{ t("draftEditor.optionalAssetsHint") }}</small></div><label class="btn upload"><FilePlus2 :size="16" />{{ t("draftEditor.addAsset") }}<input type="file" @change="uploadTaskAsset(task.id, $event)" /></label></header>
          <div class="asset-list">
            <button v-for="asset in task.assets" :key="asset.id" class="asset-row" @click="previewAsset = asset"><span>{{ asset.originalName }}</span><Eye :size="16" /></button>
            <p v-if="!task.assets.length" class="muted">{{ t("draftEditor.noAssets") }}</p>
          </div>
          <div v-if="project.conversationAssets.length" class="conversation-assets">
            <small>{{ t("draftEditor.archiveHint") }}</small>
            <button v-for="asset in project.conversationAssets.filter(asset => !task.assets.some(item => item.id === asset.id))" :key="asset.id" class="btn" @click="archiveConversationAsset(asset.id, task.id)">{{ t("draftEditor.archive", { name: asset.originalName }) }}</button>
          </div>
        </section>
        <button class="task-confirm" :class="{ confirmed: task.client_reviewed }" type="button" :disabled="saving" @click="confirmTask(task)"><CheckCircle2 :size="18" />{{ task.client_reviewed ? t("draftEditor.confirmedItem") : t("draftEditor.confirmItem") }}</button>
        <details class="task-advanced">
          <summary>{{ t("draftEditor.executionDetails") }} <small>{{ t("draftEditor.concreteSteps", { count: task.checklist.filter(item => item.parent_item_id).length }) }}</small></summary>
          <label class="advanced-acceptance">{{ t("draftEditor.acceptanceLines") }}<textarea v-model="taskForms[task.id].acceptance" v-autosize class="textarea small"></textarea></label>
          <section class="todo-list">
            <h3>{{ t("draftEditor.twoLevel") }}</h3>
            <article v-for="group in checklistGroups(task)" :key="group.id" class="todo-group">
              <div class="todo-fields todo-fields--group">
                <span class="level-tag">{{ t("draftEditor.levelOne") }}</span>
                <input v-model="todoForms[group.id].title" class="input" :aria-label="t('draftEditor.levelOne')" />
                <textarea v-model="todoForms[group.id].description" v-autosize class="textarea" :placeholder="t('draftEditor.stageGoal')"></textarea>
                <textarea v-model="todoForms[group.id].acceptance" v-autosize class="textarea" :placeholder="t('draftEditor.stageAcceptance')"></textarea>
                <label>{{ t("draftEditor.start") }}<input v-model="todoForms[group.id].plannedStartAt" class="input" type="date" /></label>
                <label>{{ t("draftEditor.end") }}<input v-model="todoForms[group.id].plannedEndAt" class="input" type="date" /></label>
                <button class="icon-btn" :title="t('common.save')" @click="saveTodo(group.id, group.row_version)"><Save :size="16" /></button>
                <button class="icon-btn danger" :title="t('common.delete')" @click="run(() => store.deleteChecklist(group.id))"><Trash2 :size="16" /></button>
              </div>
              <div v-for="item in checklistChildren(task, group.id)" :key="item.id" class="todo-fields todo-fields--child">
                <span class="level-tag">{{ t("draftEditor.levelTwo") }}</span>
                <input v-model="todoForms[item.id].title" class="input" :aria-label="t('draftEditor.levelTwo')" />
                <textarea v-model="todoForms[item.id].description" v-autosize class="textarea" :placeholder="t('draftEditor.stepDescription')"></textarea>
                <textarea v-model="todoForms[item.id].acceptance" v-autosize class="textarea" :placeholder="t('draftEditor.stepAcceptance')"></textarea>
                <label>{{ t("draftEditor.start") }}<input v-model="todoForms[item.id].plannedStartAt" class="input" type="date" /></label>
                <label>{{ t("draftEditor.end") }}<input v-model="todoForms[item.id].plannedEndAt" class="input" type="date" /></label>
                <button class="icon-btn" :title="t('common.save')" @click="saveTodo(item.id, item.row_version)"><Save :size="16" /></button>
                <button class="icon-btn danger" :title="t('common.delete')" @click="run(() => store.deleteChecklist(item.id))"><Trash2 :size="16" /></button>
              </div>
              <div class="add-row add-row--child"><input v-model="newChildTitles[group.id]" class="input" :placeholder="t('draftEditor.addStep')" @keyup.enter="addTodo(task.id, group.id)" /><button class="btn" @click="addTodo(task.id, group.id)"><Plus :size="16" />{{ t("draftEditor.addStep") }}</button></div>
            </article>
            <div class="add-row"><input v-model="newGroupTitles[task.id]" class="input" :placeholder="t('draftEditor.addStage')" @keyup.enter="addTodo(task.id)" /><button class="btn" @click="addTodo(task.id)"><Plus :size="16" />{{ t("draftEditor.addStage") }}</button></div>
          </section>
        </details>
      </article>
      <div class="add-task"><input v-model="newTaskTitle" class="input" :placeholder="t('draftEditor.newTask')" @keyup.enter="addTask" /><button class="btn" @click="addTask"><Plus :size="16" />{{ t("draftEditor.newTask") }}</button></div>
    </section>
    <section class="mobile-submit-page" :class="{ 'mobile-active': mobilePage === submitPageIndex }">
      <header><span>{{ t("draftEditor.finalStep") }}</span><h2>{{ t("draftEditor.submitReview") }}</h2><p>{{ t("draftEditor.submitHint") }}</p></header>
      <div v-if="!validation.valid" class="validation mobile-validation">
        <b>{{ t("draftEditor.validationMissing", { count: validation.errors.length }) }}</b>
        <ul><li v-for="error in validation.errors" :key="`${error.code}-${error.taskId || 0}`">{{ error.message }}</li></ul>
      </div>
      <div v-else class="validation valid mobile-validation"><CheckCircle2 :size="18" /><b>{{ t("draftEditor.validationPassed") }}</b></div>
      <button class="btn btn--primary submit" :disabled="saving || !validation.valid" @click="submit">
        <Send :size="17" />{{ t("draftEditor.submitReview") }}
      </button>
    </section>
    <nav class="mobile-draft-nav" :aria-label="t('draftEditor.switchPages')">
      <button type="button" :title="t('common.previous')" :disabled="mobilePage === 0" @click="showMobilePage(mobilePage - 1)"><ChevronLeft :size="20" /></button>
      <span>{{ mobilePage + 1 }} / {{ mobilePageCount }} · {{ mobilePageLabel }}</span>
      <button type="button" :title="t('common.next')" :disabled="mobilePage >= mobilePageCount - 1" @click="showMobilePage(mobilePage + 1)"><ChevronRight :size="20" /></button>
    </nav>
    <AssetPreviewModal :asset="previewAsset" @close="previewAsset = null" />
  </div>
</template>

<style scoped>
.draft-page{min-height:calc(100vh - 64px);background:#f5f7f7}.draft-head{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding:26px 30px;background:#fff;border-bottom:1px solid var(--line)}.draft-head span{color:var(--brand-dark);font-size:12px;font-weight:700}.draft-head h1{margin:3px 0;font-size:24px}.draft-head p,.tasks header p,.requirements-review header p{margin:0;color:var(--muted)}.submit,.meta-band .btn,.save-task,.add-row .btn,.add-task .btn,.upload,.requirements-review .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px}.meta-band{display:grid;grid-template-columns:minmax(220px,1fr) 180px 180px auto;align-items:end;gap:12px;padding:18px 30px;background:#fff;border-bottom:1px solid var(--line)}label{display:grid;gap:5px;color:var(--muted);font-size:12px}.textarea{overflow-y:hidden;resize:none}.validation,.solution-band,.requirements-review,.tasks{margin:18px 30px;padding:17px 20px;background:#fff;border:1px solid var(--line)}.validation{border-left:4px solid #d98b21}.validation.valid{display:flex;align-items:center;gap:8px;border-left-color:var(--success);color:var(--success)}.validation ul{margin:8px 0 0;padding-left:18px}.solution-band h2,.requirements-review h2,.tasks h2,.task-block h3,.mobile-submit-page h2{margin:0}.solution-band p{white-space:pre-wrap;line-height:1.7}.requirements-review{display:grid;gap:10px}.requirements-review>header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.requirement-row{display:grid;grid-template-columns:130px minmax(150px,.7fr) minmax(240px,1.3fr) 120px 36px;align-items:center;gap:7px;padding:9px 0;border-top:1px solid var(--line)}.requirement-row .textarea{min-height:58px}.confirm-check{display:flex;align-items:center;gap:6px;color:var(--ink)}.save-requirements{justify-self:start}.tasks{display:grid;gap:14px}.tasks>header{display:flex;justify-content:space-between}.task-block{border-top:2px solid var(--ink);padding:0 0 18px;background:#fff}.task-block>header{display:grid;grid-template-columns:30px minmax(0,1fr) auto;align-items:center;gap:10px;min-height:54px;border-bottom:1px solid var(--line)}.task-block>header>span{display:grid;width:26px;height:26px;place-items:center;background:var(--ink);color:#fff}.task-block>header>div b,.task-block>header>div small{display:block}.task-block>header>div small{margin-top:3px;color:var(--muted);font-size:11px}.icon-btn{display:grid;width:36px;height:36px;place-items:center;border:1px solid var(--line);background:#fff}.icon-btn.danger{color:var(--danger)}.poster-fields{display:grid;grid-template-columns:minmax(220px,1fr) 150px 150px auto;align-items:end;gap:10px;padding:16px 0}.source-field,.team-brief{grid-column:1/-1}.source-field .textarea{min-height:150px;color:var(--ink);font-family:inherit;line-height:1.65}.source-field small,.assets header small{color:var(--muted)}.team-brief{padding:12px;border-left:3px solid var(--brand);background:#f6f8f8}.team-brief span{color:var(--brand-dark);font-size:11px;font-weight:700}.team-brief p{margin:5px 0 0;line-height:1.6}.textarea.small{min-height:64px}.todo-list,.assets{padding:14px 0;border-top:1px solid var(--line)}.todo-list h3,.assets h3{font-size:15px}.task-confirm{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;min-height:44px;margin:4px 0 12px;border:1px solid var(--brand);background:#fff;color:var(--brand-dark);font-weight:700}.task-confirm.confirmed{border-color:var(--success);background:#e8f5ef;color:var(--success)}.task-advanced{border-top:1px solid var(--line)}.task-advanced>summary{display:flex;align-items:center;gap:8px;padding:14px 0;color:var(--muted);cursor:pointer;font-weight:700}.task-advanced>summary small{margin-left:auto;font-weight:400}.advanced-acceptance{padding:12px 0}.todo-group{margin-top:10px;border-left:3px solid #87979d;padding-left:10px}.todo-fields{display:grid;grid-template-columns:44px minmax(150px,.7fr) minmax(220px,1.2fr) minmax(180px,1fr) 135px 135px 36px 36px;align-items:end;gap:7px;padding:9px 0;border-bottom:1px solid var(--line)}.todo-fields--child{margin-left:22px;border-left:1px solid var(--line);padding-left:10px}.todo-fields .textarea{min-height:62px}.level-tag{align-self:center;padding:4px 5px;background:#eef2f2;color:var(--muted);font-size:11px;text-align:center}.todo-fields--child .level-tag{background:#eaf3f8;color:#2f647f}.add-row,.add-task{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin-top:10px}.add-row--child{margin-left:32px}.assets>header{display:flex;align-items:center;justify-content:space-between}.assets>header div{min-width:0}.upload input{display:none}.asset-list{display:flex;flex-wrap:wrap;gap:7px;margin-top:9px}.asset-row{display:flex;align-items:center;gap:8px;border:1px solid var(--line);padding:8px 10px;background:#fff}.conversation-assets{display:flex;align-items:center;flex-wrap:wrap;gap:7px;margin-top:10px;padding-top:10px;border-top:1px dashed var(--line)}.conversation-assets small{width:100%;color:var(--muted)}.mobile-page-status,.mobile-source-card,.mobile-draft-nav,.mobile-submit-page{display:none}
@media(max-width:1150px){.requirement-row{grid-template-columns:130px 1fr 1fr 36px}.requirement-row .textarea{grid-column:2/4}.confirm-check{grid-column:2}.todo-fields{grid-template-columns:44px minmax(180px,1fr) 1fr 1fr 36px 36px}.todo-fields label{grid-row:2}.todo-fields .textarea{grid-row:2}.todo-fields .icon-btn{grid-row:1}.todo-fields .icon-btn:last-child{grid-column:6}}
@media(max-width:850px){.meta-band{grid-template-columns:1fr 1fr}.poster-fields{grid-template-columns:1fr 1fr}.poster-fields>label:first-child,.source-field,.team-brief,.poster-fields .save-task{grid-column:1/-1}.draft-head{padding:20px 16px}.meta-band,.validation,.solution-band,.requirements-review,.tasks{margin-left:14px;margin-right:14px}.meta-band{padding:16px}.requirement-row{grid-template-columns:1fr 36px}.requirement-row .select,.requirement-row .input,.requirement-row .textarea,.confirm-check{grid-column:1}.requirement-row .icon-btn{grid-column:2;grid-row:1}.todo-fields{grid-template-columns:42px minmax(0,1fr) 36px 36px}.todo-fields .input{grid-column:2}.todo-fields .textarea,.todo-fields label{grid-column:2/-1;grid-row:auto}.todo-fields .icon-btn{grid-row:1}.todo-fields .icon-btn:last-child{grid-column:4}.todo-fields--child{margin-left:10px}.add-row--child{margin-left:10px}}
@media(max-width:700px){
  .draft-page{min-height:calc(100dvh - var(--mobile-topbar-height,56px) - 62px - env(safe-area-inset-bottom));background:#fff}
  .draft-head{display:block;padding:14px 16px 11px;border-bottom:0}.draft-head span{font-size:10px}.draft-head h1{margin-top:2px;font-size:18px;line-height:1.35}.draft-head p{display:none}.desktop-submit,.desktop-validation{display:none!important}
  .mobile-page-status{position:sticky;top:0;z-index:6;display:grid;gap:8px;padding:10px 16px 11px;border-bottom:1px solid var(--line);background:rgba(255,255,255,.97)}
  .mobile-page-status>div:first-child{display:flex;align-items:center;justify-content:space-between;gap:12px}.mobile-page-status span{min-width:0;overflow:hidden;font-size:13px;font-weight:700;text-overflow:ellipsis;white-space:nowrap}.mobile-page-status b{flex:0 0 auto;color:var(--muted);font-size:11px}
  .mobile-progress-track{height:3px;overflow:hidden;background:#e7ecec}.mobile-progress-track i{display:block;height:100%;background:var(--brand);transition:width .2s ease}
  .draft-mobile-section{display:none!important;min-height:calc(100dvh - 240px)}
  .draft-mobile-section.mobile-active{display:grid!important}
  .tasks .task-block{display:none}
  .tasks .task-block.mobile-active{display:block}
  .tasks>header{display:none}
  .mobile-submit-page.mobile-active{display:grid;gap:16px;min-height:calc(100dvh - 240px);margin:14px;padding:18px 16px;background:#fff;border:1px solid var(--line)}
  .mobile-submit-page>header span{color:var(--brand-dark);font-size:12px;font-weight:700}.mobile-submit-page>header p{margin:5px 0 0;color:var(--muted)}
  .mobile-validation{margin:0;padding:14px}
  .mobile-draft-nav{position:sticky;bottom:0;z-index:6;display:grid;grid-template-columns:44px minmax(0,1fr) 44px;align-items:center;gap:8px;padding:10px 14px calc(10px + env(safe-area-inset-bottom));border-top:1px solid var(--line);background:#fff}
  .mobile-draft-nav button{display:grid;width:44px;height:44px;place-items:center;border:1px solid var(--line);background:#fff;color:var(--ink)}.mobile-draft-nav button:disabled{opacity:.32}
  .mobile-draft-nav span{min-width:0;overflow:hidden;color:var(--muted);font-size:12px;text-align:center;text-overflow:ellipsis;white-space:nowrap}
  .meta-band{grid-template-columns:1fr;margin:14px;border:1px solid var(--line)}
  .solution-band,.requirements-review{margin:8px 0 0;padding:18px 16px;border:0;border-top:1px solid var(--line)}.tasks{margin:0;padding:0 16px;border:0}
  .task-block{border-top:0;padding-bottom:8px}.task-block>header{min-height:62px;border-bottom:0;padding-top:7px}.task-block>header>span{width:30px;height:30px;background:var(--brand-dark)}.task-block>header>div b{font-size:16px}.task-block>header>.danger{display:none}
  .requirements-review>header{display:grid}.add-row,.add-task{grid-template-columns:1fr}.add-task{margin-bottom:14px}.poster-fields{grid-template-columns:1fr;gap:13px;padding:5px 0 12px}.poster-fields>*{grid-column:1!important}.poster-fields>label:first-child,.desktop-source-field{display:none}.mobile-source-card{display:block;border:1px solid var(--line);background:#fff}.mobile-source-card>header{display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-bottom:1px solid var(--line)}.mobile-source-card>header span,.mobile-source-card>header small{display:block}.mobile-source-card>header span{color:var(--ink);font-size:13px;font-weight:700}.mobile-source-card>header small{margin-top:2px;color:var(--muted);font-size:10px}.mobile-copy-editor{min-height:230px;border:0}.poster-copy section{padding:12px;border-bottom:1px solid var(--line)}.poster-copy section:last-child{border-bottom:0}.poster-copy span{display:block;margin-bottom:5px;color:var(--brand-dark);font-size:10px;font-weight:700;text-transform:uppercase}.poster-copy p{margin:0;color:var(--ink);font-size:15px;line-height:1.55}.team-brief{padding:11px 12px;border-left:0;border-top:3px solid var(--brand);background:#f6f8f8}.team-brief p{font-size:13px}.save-task{width:100%;min-height:44px}.assets{margin-top:2px;padding:14px 0}.assets>header{align-items:flex-start;gap:10px}.assets>header h3{font-size:14px}.assets>header small{display:block;margin-top:4px;font-size:10px}.assets .upload{min-height:40px;white-space:nowrap}.task-advanced>summary{min-height:48px;font-size:13px}.conversation-assets .btn{max-width:100%;overflow:hidden;text-overflow:ellipsis}
}
</style>
